// API service for communicating with the backend

import { Farm, Pod, ArtificialReef, ReefSurroundings, CreateFarmData, CreatePodData, CreateReefData, MarineSpecies, DiveCount, DiveCountWithSpecies, ReefDiveReport, CreateMarineSpeciesData, CreateDiveCountData, ReefPredictionResult, AreaPredictionResult } from './types';
import { getEnv } from "./envConfig";

const { API_BASE_URL } = getEnv();

console.log("Using API:", API_BASE_URL);

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    // For DELETE operations or empty responses, don't try to parse JSON
    const contentType = response.headers.get('content-type');
    if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
      return {} as T; // Return empty object for void operations
    }

    return response.json();
  }

  // Farm API methods
  async getFarms(): Promise<Farm[]> {
    return this.request<Farm[]>('/api/farms');
  }

  async createFarm(farmData: CreateFarmData): Promise<Farm> {
    return this.request<Farm>('/api/farms', {
      method: 'POST',
      body: JSON.stringify(farmData),
    });
  }

  async updateFarm(id: number, farmData: Partial<CreateFarmData>): Promise<Farm> {
    return this.request<Farm>(`/api/farms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(farmData),
    });
  }

  async deleteFarm(id: number): Promise<void> {
    return this.request<void>(`/api/farms/${id}`, {
      method: 'DELETE',
    });
  }

  // Pod API methods
  async getPods(): Promise<Pod[]> {
    return this.request<Pod[]>('/api/pods');
  }

  async getFarmPods(farmId: number): Promise<Pod[]> {
    return this.request<Pod[]>(`/api/farms/${farmId}/pods`);
  }

  async createPod(podData: CreatePodData): Promise<Pod> {
    return this.request<Pod>('/api/pods', {
      method: 'POST',
      body: JSON.stringify(podData),
    });
  }

  async updatePod(id: number, podData: Partial<CreatePodData>): Promise<Pod> {
    return this.request<Pod>(`/api/pods/${id}`, {
      method: 'PUT',
      body: JSON.stringify(podData),
    });
  }

  async deletePod(id: number): Promise<void> {
    return this.request<void>(`/api/pods/${id}`, {
      method: 'DELETE',
    });
  }

  // Artificial Reef API methods
  async getReefs(): Promise<ArtificialReef[]> {
    return this.request<ArtificialReef[]>('/api/reefs');
  }

  async createReef(reefData: CreateReefData): Promise<ArtificialReef> {
    return this.request<ArtificialReef>('/api/reefs', {
      method: 'POST',
      body: JSON.stringify(reefData),
    });
  }

  async updateReef(id: number, reefData: Partial<CreateReefData>): Promise<ArtificialReef> {
    return this.request<ArtificialReef>(`/api/reefs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reefData),
    });
  }

  async deleteReef(id: number): Promise<void> {
    return this.request<void>(`/api/reefs/${id}`, {
      method: 'DELETE',
    });
  }

  async getReefSurroundings(reefId: number): Promise<ReefSurroundings> {
    return this.request<ReefSurroundings>(`/api/reefs/${reefId}/surroundings`);
  }

  // Marine Species API methods
  async getAllMarineSpecies(): Promise<MarineSpecies[]> {
    return this.request<MarineSpecies[]>('/api/species');
  }

  async getMarineSpeciesByCategory(category: string): Promise<MarineSpecies[]> {
    return this.request<MarineSpecies[]>(`/api/species/category/${category}`);
  }

  async getMarineSpeciesById(id: number): Promise<MarineSpecies> {
    return this.request<MarineSpecies>(`/api/species/${id}`);
  }

  async createMarineSpecies(speciesData: CreateMarineSpeciesData): Promise<MarineSpecies> {
    return this.request<MarineSpecies>('/api/species', {
      method: 'POST',
      body: JSON.stringify(speciesData),
    });
  }

  async updateMarineSpecies(id: number, speciesData: Partial<CreateMarineSpeciesData>): Promise<MarineSpecies> {
    return this.request<MarineSpecies>(`/api/species/${id}`, {
      method: 'PUT',
      body: JSON.stringify(speciesData),
    });
  }

  async deleteMarineSpecies(id: number): Promise<void> {
    return this.request<void>(`/api/species/${id}`, {
      method: 'DELETE',
    });
  }

  // Dive Count API methods
  async getDiveCounts(): Promise<DiveCountWithSpecies[]> {
    return this.request<DiveCountWithSpecies[]>('/api/dive-counts');
  }

  async getReefDiveCounts(reefId: number): Promise<DiveCountWithSpecies[]> {
    return this.request<DiveCountWithSpecies[]>(`/api/dive-counts/reef/${reefId}`);
  }

  async getSpeciesDiveCounts(speciesId: number): Promise<DiveCount[]> {
    return this.request<DiveCount[]>(`/api/dive-counts/species/${speciesId}`);
  }

  async getDiveCount(id: number): Promise<DiveCountWithSpecies> {
    return this.request<DiveCountWithSpecies>(`/api/dive-counts/${id}`);
  }

  async createDiveCount(diveCountData: CreateDiveCountData): Promise<DiveCount> {
    return this.request<DiveCount>('/api/dive-counts', {
      method: 'POST',
      body: JSON.stringify(diveCountData),
    });
  }

  async updateDiveCount(id: number, diveCountData: Partial<CreateDiveCountData>): Promise<DiveCount> {
    return this.request<DiveCount>(`/api/dive-counts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(diveCountData),
    });
  }

  async deleteDiveCount(id: number): Promise<void> {
    return this.request<void>(`/api/dive-counts/${id}`, {
      method: 'DELETE',
    });
  }

  async getReefDiveReport(reefId: number): Promise<ReefDiveReport> {
    return this.request<ReefDiveReport>(`/api/dive-counts/reef/${reefId}/report`);
  }

  // Prediction API methods (proxied through backend)
  async getReefPrediction(reefId: number): Promise<ReefPredictionResult> {
    return this.request<ReefPredictionResult>(`/api/reefs/${reefId}/predict`, {
      method: 'POST',
    });
  }

  async getAreaPrediction(
    volumes_in_cm3: number[], 
    lons_in_decdeg_epsg4326: number[], 
    lats_in_decdeg_epsg4326: number[]
  ): Promise<AreaPredictionResult> {
    return this.request<AreaPredictionResult>(`/api/reefs/predict-area`, {
      method: 'POST',
      body: JSON.stringify({ volumes_in_cm3, lons_in_decdeg_epsg4326, lats_in_decdeg_epsg4326 }),
    });
  }
}

export const apiService = new ApiService();
